// src/components/entities/IndicatorModal.jsx
import React, { useEffect, useState } from "react";
import { X, Calendar, Info } from "lucide-react";
import TooltipHelper from "../../../../components/common/TooltipHelper";

/* ---------- helpers fecha seguros para <input type="date" /> ---------- */
const isYMD = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ""));

const toYMDForInput = (v) => {
  if (!v) return "";
  if (isYMD(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(+d)) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/* ---------- estilos reutilizables ---------- */
const input =
  "w-full border rounded-xl px-3 py-2.5 border-gray-200 text-sm focus:ring-2 focus:ring-[#0488AF] outline-none bg-white";
const labelXs = "block text-xs font-medium text-gray-700 mb-1";


export default function IndicatorModal({
  open,
  mode = "create", // "create" | "edit"
  initialData = {},
  onClose,
  onSave,
  saving = false,
}) {
  const [form, setForm] = useState({
    label: "",
    value: "",
    // "nota" → se muestra como Descripción; en backend se guarda como hint
    hint: "",
    periodicity: "",
    // fecha de la fuente
    source_date: "",
    source_label: "",
    source_url: "",
    color: "#0488AF",
  });

  useEffect(() => {
    if (!open) return;

    const rawDate = toYMDForInput(
      initialData.last_updated_at ||
        initialData.lastUpdatedAt ||
        ""
    );

    setForm({
      label: initialData.label || "",
      value: initialData.value || "",
      // soporta tanto hint actual como note legado
      hint: initialData.hint || initialData.note || "",
      periodicity: initialData.periodicity || "",
      source_date: rawDate || "",
      source_label:
        initialData.source_label || initialData.sourceLabel || "",
      source_url:
        initialData.source_url || initialData.sourceUrl || "",
      color: initialData.color || "#0488AF",
    });
  }, [open, initialData]);

  if (!open) return null;

  const setField = (field, value) =>
    setForm((f) => ({
      ...f,
      [field]: value,
    }));

  const canSave =
    String(form.label || "").trim() &&
    String(form.value || "").trim() &&
    !saving;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSave) return;

    const trimmedLabel = String(form.label || "").trim();
    const trimmedValue = String(form.value || "").trim();

    const sourceYmd = form.source_date || "";

    const payload = {
      label: trimmedLabel,
      value: trimmedValue,
      // Descripción → backend usa "hint"
      hint: String(form.hint || "").trim() || null,
      // Periodicidad
      periodicity: String(form.periodicity || "").trim() || null,
      // Fecha de la fuente
      last_updated_at: sourceYmd || null,
      // Fuente
      source_label: String(form.source_label || "").trim() || null,
      source_url: String(form.source_url || "").trim() || null,
      // Color de la tarjeta
      color: form.color || "#0488AF",
      // IMPORTANTE: ya NO incluimos prefix → todo va en value
    };

    await onSave?.(payload);
  };

  const subTitle =
    mode === "edit" ? "Editar indicador" : "Nuevo indicador";

  return (
    <div
      className="fixed inset-0 z-[220] bg-black/40 flex items-center justify-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className="w-full max-w-xl bg-white rounded-2xl shadow-xl max-h-[90vh] flex flex-col overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header tipo banner RDS */}
        <div
          className="px-5 py-4 text-white"
          style={{ background: "linear-gradient(90deg,#0c3b66,#0488AF)" }}
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              {/* Título principal pedido: Proyectos externos */}
              <div className="font-semibold text-base">
                Proyectos externos
              </div>
              <p className="text-[11px] text-white/90 mt-0.5">
                {subTitle} · sección de indicadores.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Formulario (el card completo ahora scrollea si es muy alto) */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-col"
        >
          <div className="px-5 py-4 space-y-3">
            {/* Nombre del indicador */}
            <div>
              <label className={labelXs}>
                Nombre del indicador <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={input}
                value={form.label}
                onChange={(e) => setField("label", e.target.value)}
                placeholder="Ej: Tasa de desocupación"
              />
            </div>

            {/* Valor (todo va aquí, sin prefijo) */}
            <div>
              <label className={`${labelXs} flex items-center gap-1`}>
                Valor <span className="text-red-500">*</span>
                <TooltipHelper text="El valor se guarda como texto. Escribe el número tal como aparece en la fuente (puedes usar coma o punto como separador decimal, incluir %, UF, US$, etc.)." />
              </label>
              <input
                type="text"
                className={input}
                value={form.value}
                onChange={(e) => setField("value", e.target.value)}
                placeholder="Ej: 2,5 %, US$ 1.234,50 o 1.234,56 UF"
              />
            </div>

            {/* Descripción (antes nota) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={labelXs + " mb-0"}>Descripción</label>
                <span className="text-[10px] text-gray-500">
                  {String(form.hint || "").length}/50
                </span>
              </div>
              <textarea
                className={`${input} resize-none min-h-[72px]`}
                rows={3}
                value={form.hint}
                onChange={(e) => setField("hint", e.target.value)}
                placeholder="Explica qué representa el indicador, alcance, observaciones, etc."
                maxLength={50}
              />
            </div>

            {/* Periodicidad */}
            <div>
              <label className={labelXs}>Periodicidad</label>
              <input
                type="text"
                className={input}
                value={form.periodicity}
                onChange={(e) =>
                  setField("periodicity", e.target.value)
                }
                placeholder="Ej: Diaria, Semanal, Mensual, Trimestral, Anual..."
              />
            </div>

            {/* Fecha de última actualización de la fuente */}
            <div>
              <label className={`${labelXs} flex items-center gap-1`}>
                Última actualización de la fuente
                <TooltipHelper text="Fecha según el documento o publicación oficial de donde tomaste el dato (boletín, informe, serie estadística, etc.). Esta fecha se mostrará en la tarjeta como 'Última actualización de la fuente'." />
              </label>
              <div className="relative">
                <input
                  type="date"
                  className={`${input} pr-9`}
                  value={form.source_date || ""}
                  onChange={(e) =>
                    setField("source_date", e.target.value)
                  }
                />
                <Calendar className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            {/* Fuente: título + subtítulos Etiqueta / URL */}
            <div className="border border-gray-200 rounded-xl p-3 bg-gray-50/70">
              <p className="text-xs font-semibold text-gray-800 mb-2">
                Fuente
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    Etiqueta
                  </label>
                  <input
                    type="text"
                    className={input}
                    value={form.source_label || ""}
                    onChange={(e) =>
                      setField("source_label", e.target.value)
                    }
                    placeholder="Ej: INE, Banco Central"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    URL
                  </label>
                  <input
                    type="url"
                    className={input}
                    value={form.source_url || ""}
                    onChange={(e) =>
                      setField("source_url", e.target.value)
                    }
                    placeholder="https://…"
                  />
                </div>
              </div>
            </div>

            {/* Color de la tarjeta */}
            <div>
              <label className={labelXs}>Color de la tarjeta</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) =>
                    setField("color", e.target.value)
                  }
                  className="w-10 h-8 rounded-md border border-gray-300 p-0"
                />
                <span className="text-[11px] text-gray-500">
                  Se usa para el borde y la pastilla del indicador.
                </span>
              </div>
            </div>
          </div>

          {/* Footer RDS */}
          <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-11 px-4 rounded-xl bg-[#C42251] text-white hover:bg-[#A01D45] text-sm font-semibold transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!canSave}
              className="h-11 px-4 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-semibold transition-colors"
            >
              {saving
                ? "Guardando…"
                : mode === "edit"
                ? "Guardar cambios"
                : "Crear indicador"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}